import React, { useEffect } from 'react';
import Header from '../components/layout/Header';
import Footer from '../components/layout/Footer';
import CartDrawer from '../components/cart/CartDrawer';
import { Link } from 'react-router-dom';
import { products, categories, siteImages } from '../data/mock';
import { ArrowRight, Shield, Truck, Award, Zap, Target, FlaskConical, ChevronRight, Bolt, Dumbbell, ShoppingCart, Star, Check, Package } from 'lucide-react';
import { useCart } from '../context/CartContext';

const HomePage = () => {
  const { addToCart } = useCart();
  const featuredProducts = products.slice(0, 12); // Show more products
  const topProduct = products[0]; // Feature the first product as default

  useEffect(() => {
    document.title = 'Axon Muscle | Premium Muscle Supplements & Performance Nutrition';
  }, []);

  return (
    <div className="min-h-screen bg-neutral-950">
      <Header />
      <CartDrawer />
      
      <main className="pt-[104px]">
        
        {/* HERO - Centered with Product Showcase */}
        <section className="relative py-20 lg:py-32 overflow-hidden">
          {/* Background */}
          <div className="absolute inset-0 bg-[radial-gradient(circle_at_center,_var(--tw-gradient-stops))] from-axon-950/50 via-neutral-950 to-neutral-950" />
          <div className="absolute top-0 left-1/2 -translate-x-1/2 w-[800px] h-[800px] bg-axon-600/10 rounded-full blur-[150px]" />
          
          <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center max-w-4xl mx-auto mb-16">
              <div className="inline-flex items-center gap-2 px-5 py-2.5 bg-axon-500/10 border border-axon-500/30 rounded-full text-axon-400 text-sm font-bold uppercase tracking-wider mb-8">
                <Zap className="w-4 h-4" />
                Premium Supplements
              </div>
              
              <h1 className="font-display text-5xl sm:text-6xl lg:text-8xl text-white uppercase tracking-tight leading-[0.9] mb-8">
                Unlock Your
                <span className="block text-transparent bg-clip-text bg-gradient-to-r from-axon-400 via-axon-500 to-electric-400">
                  Full Potential
                </span>
              </h1>
              
              <p className="text-xl text-neutral-400 max-w-2xl mx-auto mb-10">
                Science-backed supplements engineered for athletes who demand results. Premium quality, lab-tested formulas.
              </p>
              
              <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
                <Link 
                  to="/shop"
                  className="group inline-flex items-center gap-3 px-10 py-5 bg-gradient-to-r from-axon-500 to-axon-600 hover:from-axon-400 hover:to-axon-500 text-white font-bold uppercase tracking-wider rounded-2xl transition-all shadow-button hover:shadow-glow-cyan"
                >
                  Shop All Products
                  <ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" />
                </Link>
                <Link 
                  to="/about"
                  className="inline-flex items-center gap-2 px-10 py-5 text-white font-bold uppercase tracking-wider hover:text-axon-400 transition-colors"
                >
                  Learn More
                  <ChevronRight className="w-5 h-5" />
                </Link>
              </div>
            </div>
            
            {/* Product Showcase Row */}
            <div className="relative">
              <div className="flex items-end justify-center gap-4 lg:gap-8">
                {products.slice(0, 5).map((product, i) => (
                  <Link
                    key={product.id}
                    to={`/product/${product.slug}`}
                    className={`group relative transition-all duration-500 ${
                      i === 2 ? 'w-48 lg:w-64 z-10' : 'w-32 lg:w-44 opacity-70 hover:opacity-100'
                    }`}
                  >
                    <div className={`bg-neutral-900/50 rounded-2xl overflow-hidden border border-neutral-800 group-hover:border-axon-500/50 transition-all ${
                      i === 2 ? 'shadow-glow-cyan' : ''
                    }`}>
                      {product.image ? (
                        <div className="w-full aspect-square flex items-center justify-center p-3">
                          <img src={product.image} alt={product.name} className="max-w-full max-h-full object-contain" />
                        </div>
                      ) : (
                        <div className="w-full aspect-square bg-neutral-800 flex items-center justify-center">
                          <Package className="w-12 h-12 text-neutral-600" />
                        </div>
                      )}
                    </div>
                    {i === 2 && (
                      <div className="absolute -bottom-4 left-1/2 -translate-x-1/2 px-4 py-2 bg-axon-500 text-white text-xs font-bold uppercase tracking-wider rounded-full whitespace-nowrap">
                        Best Seller
                      </div>
                    )}
                  </Link>
                ))}
              </div>
            </div>
          </div>
        </section>

        {/* TRUST BAR - Horizontal Icons */}
        <section className="py-8 bg-neutral-900 border-y border-neutral-800">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="flex flex-wrap items-center justify-center gap-8 lg:gap-16">
              {[
                { icon: FlaskConical, label: '100% Lab Tested' },
                { icon: Shield, label: 'Premium Quality' },
                { icon: Truck, label: 'Free Shipping $99+' },
                { icon: Award, label: '30-Day Guarantee' },
              ].map((item, i) => (
                <div key={i} className="flex items-center gap-3">
                  <item.icon className="w-6 h-6 text-axon-500" />
                  <span className="text-white font-semibold text-sm">{item.label}</span>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* CATEGORIES - Full Width Scrolling */}
        <section className="py-16 bg-neutral-950">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 mb-10">
            <div className="flex items-end justify-between">
              <div>
                <span className="text-axon-500 text-sm font-bold uppercase tracking-wider">Categories</span>
                <h2 className="font-display text-3xl lg:text-4xl text-white uppercase tracking-tight mt-2">
                  Shop By Goal
                </h2>
              </div>
              <Link to="/shop" className="hidden sm:flex items-center gap-2 text-neutral-400 hover:text-axon-400 font-semibold transition-colors">
                View All <ArrowRight className="w-4 h-4" />
              </Link>
            </div>
          </div>
          
          <div className="overflow-x-auto scrollbar-hide">
            <div className="flex gap-4 px-4 sm:px-6 lg:px-8 pb-4 min-w-max lg:min-w-0 lg:grid lg:grid-cols-6 max-w-7xl mx-auto">
              {categories.map((cat) => (
                <Link
                  key={cat.id}
                  to={`/shop?category=${cat.slug}`}
                  className="group flex-shrink-0 w-40 lg:w-auto"
                >
                  <div className="bg-neutral-900 border border-neutral-800 group-hover:border-axon-500/50 rounded-2xl p-5 text-center transition-all group-hover:bg-neutral-900/80">
                    <div className="w-14 h-14 bg-axon-600/20 rounded-xl flex items-center justify-center mx-auto mb-4 group-hover:bg-axon-600/30 transition-colors">
                      <Dumbbell className="w-7 h-7 text-axon-500" />
                    </div>
                    <h3 className="text-white font-bold text-sm uppercase tracking-wide group-hover:text-axon-400 transition-colors">
                      {cat.name}
                    </h3>
                  </div>
                </Link>
              ))}
            </div>
          </div>
        </section>

        {/* FEATURED PRODUCT - Large Hero Card */}
        <section className="py-20 bg-gradient-to-b from-neutral-950 via-axon-950/20 to-neutral-950">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="relative bg-neutral-900 rounded-3xl overflow-hidden border border-neutral-800">
              <div className="absolute inset-0 bg-gradient-to-r from-axon-600/10 to-transparent" />
              
              <div className="relative grid lg:grid-cols-2 gap-8 p-8 lg:p-12">
                {/* Content */}
                <div className="flex flex-col justify-center order-2 lg:order-1">
                  <span className="inline-flex items-center gap-2 text-axon-400 text-sm font-bold uppercase tracking-wider mb-4">
                    <Star className="w-4 h-4 fill-axon-400" />
                    Featured Product
                  </span>
                  <h2 className="font-display text-3xl lg:text-5xl text-white uppercase tracking-tight mb-4">
                    {topProduct?.name}
                  </h2>
                  <p className="text-neutral-400 text-lg mb-6 max-w-md">
                    {topProduct?.description}
                  </p>
                  
                  <div className="flex flex-wrap gap-3 mb-8">
                    {topProduct?.keyBenefits?.map((benefit, i) => (
                      <span key={i} className="inline-flex items-center gap-2 px-4 py-2 bg-neutral-800 rounded-lg text-neutral-300 text-sm">
                        <Check className="w-4 h-4 text-axon-500" />
                        {benefit}
                      </span>
                    ))}
                  </div>
                  
                  <div className="flex items-center gap-6">
                    <span className="text-4xl font-bold text-white">${topProduct?.price}</span>
                    <button
                      onClick={() => topProduct && addToCart(topProduct)}
                      className="inline-flex items-center gap-3 px-8 py-4 bg-gradient-to-r from-axon-500 to-axon-600 hover:from-axon-400 hover:to-axon-500 text-white font-bold uppercase tracking-wider rounded-xl transition-all shadow-button"
                    >
                      <ShoppingCart className="w-5 h-5" />
                      Add to Cart
                    </button>
                  </div>
                </div>
                
                {/* Image */}
                <div className="order-1 lg:order-2 flex items-center justify-center">
                  <div className="relative w-full max-w-sm">
                    {topProduct?.image ? (
                      <img src={topProduct.image} alt={topProduct.name} className="w-full rounded-2xl" />
                    ) : (
                      <div className="w-full aspect-square bg-neutral-800 rounded-2xl flex items-center justify-center">
                        <Package className="w-20 h-20 text-neutral-600" />
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* ALL PRODUCTS GRID - 2 Rows */}
        <section className="py-20 bg-neutral-950">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-12">
              <span className="text-axon-500 text-sm font-bold uppercase tracking-wider">Our Products</span>
              <h2 className="font-display text-3xl lg:text-5xl text-white uppercase tracking-tight mt-2">
                Complete Collection
              </h2>
            </div>
            
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 lg:gap-6">
              {featuredProducts.map((product) => (
                <Link
                  key={product.id}
                  to={`/product/${product.slug}`}
                  className="group bg-neutral-900 rounded-2xl overflow-hidden border border-neutral-800 hover:border-axon-500/50 transition-all"
                >
                  <div className="relative aspect-square bg-neutral-800 overflow-hidden flex items-center justify-center p-3">
                    {product.image ? (
                      <img 
                        src={product.image} 
                        alt={product.name} 
                        className="max-w-full max-h-full object-contain group-hover:scale-105 transition-transform duration-500" 
                      />
                    ) : (
                      <div className="w-full h-full flex items-center justify-center">
                        <Package className="w-16 h-16 text-neutral-600" />
                      </div>
                    )}
                    {product.badge && (
                      <span className="absolute top-3 left-3 px-3 py-1 bg-axon-600 text-white text-xs font-bold uppercase rounded-lg">
                        {product.badge}
                      </span>
                    )}
                  </div>
                  <div className="p-4">
                    <p className="text-axon-500 text-xs font-semibold uppercase tracking-wider mb-1">
                      {categories.find(c => c.id === product.categoryId)?.name}
                    </p>
                    <h3 className="text-white font-semibold text-sm mb-2 line-clamp-2 min-h-[40px] group-hover:text-axon-400 transition-colors">
                      {product.name}
                    </h3>
                    <div className="flex items-center justify-between">
                      <span className="text-white font-bold text-lg">${product.price}</span>
                      <span className="text-xs text-green-400 font-medium">In Stock</span>
                    </div>
                  </div>
                </Link>
              ))}
            </div>
            
            <div className="text-center mt-10">
              <Link 
                to="/shop"
                className="inline-flex items-center gap-2 px-8 py-4 border-2 border-axon-500 text-axon-500 hover:bg-axon-500 hover:text-white font-bold uppercase tracking-wider rounded-xl transition-all"
              >
                View All Products
                <ArrowRight className="w-5 h-5" />
              </Link>
            </div>
          </div>
        </section>

        {/* WHY CHOOSE US - 3 Column Cards */}
        <section className="py-20 bg-neutral-900">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-14">
              <span className="text-axon-500 text-sm font-bold uppercase tracking-wider">Why Axon Muscle</span>
              <h2 className="font-display text-3xl lg:text-5xl text-white uppercase tracking-tight mt-2">
                The Axon Difference
              </h2>
            </div>
            
            <div className="grid md:grid-cols-3 gap-8">
              {[
                {
                  icon: FlaskConical,
                  title: 'Lab Tested',
                  desc: 'Every batch is third-party tested for purity, potency, and safety. We never compromise on quality.',
                  highlight: '100%'
                },
                {
                  icon: Target,
                  title: 'Science-Backed',
                  desc: 'Our formulas are based on the latest research to deliver real, measurable results you can feel.',
                  highlight: 'Proven'
                },
                {
                  icon: Award,
                  title: 'Made in USA',
                  desc: 'Manufactured in GMP-certified facilities with the highest quality standards in the industry.',
                  highlight: 'Premium'
                },
              ].map((item, i) => (
                <div key={i} className="relative bg-neutral-950 rounded-3xl p-8 border border-neutral-800 overflow-hidden group hover:border-axon-500/30 transition-all">
                  <div className="absolute top-0 right-0 text-[120px] font-display font-bold text-neutral-900 leading-none select-none">
                    {item.highlight}
                  </div>
                  <div className="relative">
                    <div className="w-16 h-16 bg-gradient-to-br from-axon-500 to-axon-600 rounded-2xl flex items-center justify-center mb-6">
                      <item.icon className="w-8 h-8 text-white" />
                    </div>
                    <h3 className="font-display text-2xl text-white uppercase tracking-wide mb-4">{item.title}</h3>
                    <p className="text-neutral-400 leading-relaxed">{item.desc}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* STATS BAR */}
        <section className="py-16 bg-gradient-to-r from-axon-700 via-axon-600 to-axon-700">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-8 text-center">
              {[
                { value: `${products.length}`, label: 'Premium Products' },
                { value: '100%', label: 'Lab Tested' },
                { value: 'USA', label: 'Manufactured' },
                { value: '30-Day', label: 'Money Back' },
              ].map((stat, i) => (
                <div key={i}>
                  <p className="font-display text-4xl lg:text-5xl text-white font-bold mb-2">{stat.value}</p>
                  <p className="text-axon-200 text-sm uppercase tracking-wider">{stat.label}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* CTA - Full Width */}
        <section className="relative py-24 overflow-hidden">
          <div className="absolute inset-0">
            <img 
              src={siteImages.ctaBackground}
              alt="Fitness"
              className="w-full h-full object-cover opacity-30"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-neutral-950 via-neutral-950/90 to-neutral-950" />
          </div>
          
          <div className="relative max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
            <Bolt className="w-12 h-12 text-axon-500 mx-auto mb-6" />
            <h2 className="font-display text-4xl lg:text-6xl text-white uppercase tracking-tight mb-6">
              Ready to Level Up?
            </h2>
            <p className="text-neutral-400 text-lg mb-10 max-w-2xl mx-auto">
              Join athletes who trust Axon Muscle for their supplement needs. Start your transformation today.
            </p>
            <Link 
              to="/shop"
              className="inline-flex items-center gap-3 px-12 py-5 bg-gradient-to-r from-axon-500 to-axon-600 hover:from-axon-400 hover:to-axon-500 text-white font-bold uppercase tracking-wider rounded-2xl transition-all shadow-button hover:shadow-glow-cyan"
            >
              Start Shopping
              <ArrowRight className="w-5 h-5" />
            </Link>
          </div>
        </section>

      </main>
      
      <Footer />
    </div>
  );
};

export default HomePage;
